import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import {
    SecurityCheckModalsGroup,
    MfaFlow,
} from '@accountFederation/modules/securityCheck/faceId/mfaFlows';
import { MfaPayload } from '@accountFederation/modules/securityCheck/types';
import { useMfaStatusCheck } from '@accountFederation/modules/securityCheck/useMfaStatusCheck';
import { isBrazil } from '@modules/layout/utils/isCountry';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import modalIndex from '@store/modules/ui/shared/modal/modalIndex';
import { WithdrawalFaceId } from '@superbet-group/product.analytics';
import { recordDurationSecondsStart } from '@superbet-group/web.observability.sdk-hooks';
import { getPlayerVerificationLabels } from '@core/utils/observability';

interface UserVerificationChecks {
    triggerVerificationFor: MfaFlow
}

// Track Brazil analytics for withdrawal faceId
const trackWithdrawalFaceIdForBrazil = (name: 'Withdrawal_faceId', payload?: Omit<WithdrawalFaceId, 'name'>) => {
    const isBrazilDomain = isBrazil();
    const analyticsInstance = AnalyticsSDK.getInstance();

    if (isBrazilDomain && analyticsInstance) {
        analyticsInstance.log(name, payload);
    }
};

export function useUserVerificationChecks({ triggerVerificationFor }: UserVerificationChecks) {
    const modalModule = useVuexStore<typeof modalIndex>('ui/modal');
    const setModal = modalModule.action('setModal');
    const closeModal = modalModule.action('closeModal');
    const { checkMfaStatus } = useMfaStatusCheck();

    function onFaceIdCheck(onSuccessAction:(payload: MfaPayload | undefined) => void) {
        trackWithdrawalFaceIdForBrazil('Withdrawal_faceId', {
            eventAction: 'open_face_id',
            eventLabel: 'verify_user_attempt',
            timestamp: new Date().toISOString(),
        });

        recordDurationSecondsStart('player_verification', '0_verification_required', getPlayerVerificationLabels({
            user_flow: MfaFlow.BankAccountManagement,
        }));

        const modalParameters = { group: SecurityCheckModalsGroup };
        const callOnComplete = (mfaPayload: MfaPayload) => {
            const verifiedAction = () => {
                trackWithdrawalFaceIdForBrazil('Withdrawal_faceId', {
                    eventAction: 'open_face_id',
                    eventLabel: 'verify_user_success',
                    timestamp: new Date().toISOString(),
                });

                onSuccessAction(mfaPayload);
                closeModal(modalParameters);
            };
            const failedAction = () => {
                trackWithdrawalFaceIdForBrazil('Withdrawal_faceId', {
                    eventAction: 'open_face_id',
                    eventLabel: 'verify_user_failed',
                    timestamp: new Date().toISOString(),
                });

                setModal({
                    code: modalCodes.securityCheckError,
                    data: {
                        ...mfaPayload,
                        primaryCtaAction: () => closeModal(modalParameters),
                    },
                });
            };
            checkMfaStatus({
                mfaPayload,
                verifiedAction,
                failedAction,
            });
        };
        setModal({
            code: modalCodes.getIdVerification,
            data: {
                action: 'faceid',
                callOnComplete: callOnComplete,
                flowName: triggerVerificationFor,
            },
            ...modalParameters,
        });
    }
    return { onFaceIdCheck };
}
